import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import type { Page } from '@blackblack/shared';

/**
 * Minimal data cache (stale-while-revalidate) — the subset of react-query we
 * need, with no dependency. Keys are strings; `invalidate(prefix)` refetches.
 */
interface Entry<T> {
  data?: T;
  error?: unknown;
  promise?: Promise<void>;
  updatedAt: number;
  fetcher?: () => Promise<T>;
}

const cache = new Map<string, Entry<unknown>>();
const subs = new Map<string, Set<() => void>>();

function notify(key: string) {
  subs.get(key)?.forEach((l) => l());
}

function subscribe(key: string, l: () => void) {
  let set = subs.get(key);
  if (!set) subs.set(key, (set = new Set()));
  set.add(l);
  return () => {
    set!.delete(l);
  };
}

function load<T>(key: string, fetcher: () => Promise<T>) {
  const entry = (cache.get(key) ?? { updatedAt: 0 }) as Entry<T>;
  entry.fetcher = fetcher;
  if (entry.promise) return entry.promise;
  entry.promise = fetcher()
    .then((data) => {
      cache.set(key, { data, updatedAt: Date.now(), fetcher });
    })
    .catch((error: unknown) => {
      cache.set(key, { ...entry, error, promise: undefined, updatedAt: Date.now() });
    })
    .finally(() => notify(key));
  cache.set(key, entry);
  notify(key);
  return entry.promise;
}

export interface QueryResult<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  refetch: () => Promise<void>;
}

export function useQuery<T>(key: string | null, fetcher: () => Promise<T>, opts: { staleMs?: number } = {}): QueryResult<T> {
  const staleMs = opts.staleMs ?? 30_000;
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const entry = useSyncExternalStore(
    useCallback((l) => (key ? subscribe(key, l) : () => {}), [key]),
    () => (key ? (cache.get(key) as Entry<T> | undefined) : undefined),
  );
  useEffect(() => {
    if (!key) return;
    const e = cache.get(key);
    if (!e || (!e.promise && Date.now() - e.updatedAt > staleMs) || e.error) void load(key, () => fetcherRef.current());
  }, [key, staleMs]);
  return {
    data: entry?.data,
    error: entry?.data === undefined ? entry?.error : undefined,
    loading: !!key && entry?.data === undefined && !entry?.error,
    refetch: () => (key ? load(key, () => fetcherRef.current()) : Promise.resolve()),
  };
}

export function setQueryData<T>(key: string, updater: (prev: T | undefined) => T | undefined) {
  const e = (cache.get(key) ?? { updatedAt: Date.now() }) as Entry<T>;
  cache.set(key, { ...e, data: updater(e.data), error: undefined });
  notify(key);
}

/** Marks matching queries stale and refetches the ones that are mounted. */
export function invalidate(prefix: string) {
  for (const [key, e] of cache) {
    if (!key.startsWith(prefix)) continue;
    e.updatedAt = 0;
    if (subs.get(key)?.size && e.fetcher) void load(key, e.fetcher);
  }
}

/* ---------- infinite lists ---------- */

interface InfiniteState<T> {
  pages: T[][];
  nextCursor: string | null;
  done: boolean;
  loading: boolean;
  error: unknown;
}

export interface InfiniteResult<T> {
  items: T[];
  loading: boolean;
  initialLoading: boolean;
  error: unknown;
  hasMore: boolean;
  loadMore: () => void;
  refresh: () => Promise<void>;
}

export function useInfinite<T>(key: string | null, fetchPage: (cursor: string | null) => Promise<Page<T>>): InfiniteResult<T> {
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  const stateKey = key ? `inf:${key}` : null;

  const state = useSyncExternalStore(
    useCallback((l) => (stateKey ? subscribe(stateKey, l) : () => {}), [stateKey]),
    () => (stateKey ? (cache.get(stateKey)?.data as InfiniteState<T> | undefined) : undefined),
  );

  const fetchNext = useCallback(
    async (reset = false) => {
      if (!stateKey) return;
      const prev = (cache.get(stateKey)?.data as InfiniteState<T> | undefined) ?? { pages: [], nextCursor: null, done: false, loading: false, error: null };
      if (prev.loading || (prev.done && !reset)) return;
      const base = reset ? { pages: [], nextCursor: null, done: false, loading: true, error: null } : { ...prev, loading: true, error: null };
      cache.set(stateKey, { data: base, updatedAt: Date.now(), fetcher: () => Promise.resolve(base) });
      notify(stateKey);
      try {
        const page = await fetchRef.current(reset ? null : prev.nextCursor);
        const next: InfiniteState<T> = { pages: [...base.pages, page.items], nextCursor: page.nextCursor, done: !page.nextCursor, loading: false, error: null };
        cache.set(stateKey, { data: next, updatedAt: Date.now() });
      } catch (error) {
        cache.set(stateKey, { data: { ...base, loading: false, error }, updatedAt: Date.now() });
      }
      notify(stateKey);
    },
    [stateKey],
  );

  useEffect(() => {
    if (stateKey && !cache.get(stateKey)) void fetchNext(true);
  }, [stateKey, fetchNext]);

  const items = state ? state.pages.flat() : [];
  return {
    items,
    loading: !!state?.loading,
    initialLoading: !!stateKey && (!state || (state.loading && state.pages.length === 0)),
    error: state?.error,
    hasMore: !!state && !state.done,
    loadMore: () => void fetchNext(false),
    refresh: () => fetchNext(true),
  };
}

/** Drops cached infinite lists so the next mount refetches. */
export function resetInfinite(prefix: string) {
  for (const key of [...cache.keys()]) if (key.startsWith(`inf:${prefix}`)) cache.delete(key);
}

export function clearAllQueries() {
  cache.clear();
  subs.forEach((s) => s.forEach((l) => l()));
}
